import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "@/trpc";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import type { WorkspaceProvider } from "./workspace-types";

export function WorkspacePools({
  provider,
  organizationBusinessUnitId,
}: {
  provider: WorkspaceProvider;
  organizationBusinessUnitId: string;
}) {
  const [open, setOpen] = useState(false);
  const trpc = useTRPC();
  const query = useQuery({
    ...trpc.providerWorkspace.poolDetails.queryOptions({
      organizationBusinessUnitId,
      affiliationId: provider.affiliationId,
      providerUserId: provider.providerUserId,
    }),
    enabled: open,
  });
  if (!provider.poolCount) {
    return <span className="text-muted-foreground">—</span>;
  }
  const remaining = provider.poolCount - provider.pools.length;
  return (
    <div
      className="max-w-48 text-sm"
      onClick={(event) => event.stopPropagation()}
    >
      {provider.pools.map((pool) => (
        <div key={pool.id} className="truncate" title={pool.name}>
          {pool.name}
        </div>
      ))}
      {remaining > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0 text-xs"
              aria-label={`Show ${remaining} more pools for ${provider.name}`}
            >
              {remaining} more
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-h-72 overflow-y-auto">
            <p className="mb-2 text-sm font-medium">Provider pools</p>
            {query.isPending && (
              <p role="status" className="text-sm text-muted-foreground">
                Loading pools…
              </p>
            )}
            {query.isError && (
              <div role="alert">
                <p className="text-sm">Could not load pools.</p>
                <Button variant="link" onClick={() => void query.refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {query.data && !query.isError && (
              <ul className="space-y-1 text-sm">
                {query.data.map((pool) => (
                  <li key={pool.id}>{pool.name}</li>
                ))}
              </ul>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
